import axios from "axios";
import { refreshSession } from "./authClient";

const API_BASE = process.env.REACT_APP_API_URL || "";

// Create an axios instance
const apiClient = axios.create({
    baseURL: API_BASE,
    withCredentials: true
});

// Add response interceptor for 401 handling
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                await refreshSession();
                return apiClient(originalRequest);
            } catch (refreshError) {
                // Refresh failed, redirect to login
                window.location.href = "/login";
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export async function getLinkToken() {
    const resp = await apiClient.post(`/api/plaid/link-token`);
    return resp.data.link_token;
}

export async function exchangePublicToken(publicToken) {
    const resp = await apiClient.post(
        `/api/plaid/exchange`,
        { publicToken },
        { headers: { "Content-Type": "application/json" } }
    );
    return resp.data;
}

export async function getAccounts(excludeHidden = true, signal) {
    const resp = await apiClient.get(`/api/plaid/accounts`, {
        params: { excludeHidden },
        signal
    });
    return resp.data;
}

export async function getHoldings(signal) {
    const resp = await apiClient.get(`/api/plaid/holdings`, { signal });
    return resp.data;
}

export async function syncTransactions(signal) {
    const resp = await apiClient.get(`/api/plaid/transactions/sync`, { signal });
    return resp.data;
}

export async function getTransactionsByAccount(accountId, signal) {
    const resp = await apiClient.get(`/api/plaid/transactions/${accountId}`, { signal });
    return resp.data;
}

// Net Worth API
export async function getNetWorth() {
    const resp = await apiClient.get(`/api/networth`);
    return resp.data;
}

export async function saveNetWorthSnapshot() {
    const resp = await apiClient.post(`/api/networth/snapshot`);
    return resp.data;
}

export async function getNetWorthHistory(days = 30) {
    const resp = await apiClient.get(`/api/networth/history`, {
        params: { days }
    });
    return resp.data;
}

export async function getSyncStatus() {
    const resp = await apiClient.get(`/api/networth/sync-status`);
    return resp.data;
}

export async function getNetWorthDigest(signal) {
    const resp = await apiClient.get(`/api/networth/digest`, { signal });
    return resp.data;
}

// Account Links API
export async function getAccountLinks(signal) {
    const resp = await apiClient.get(`/api/plaid/links`, { signal });
    return resp.data;
}

export async function unlinkAccount(linkId) {
    const resp = await apiClient.delete(`/api/plaid/links/${linkId}`);
    return resp.data;
}

export async function getReconnectToken(linkId) {
    const resp = await apiClient.post(`/api/plaid/links/${linkId}/reconnect`);
    return resp.data.link_token;
}

export async function markReconnectSuccess(linkId) {
    const resp = await apiClient.post(`/api/plaid/links/${linkId}/reconnect-success`);
    return resp.data;
}

// Hidden Accounts API
export async function getHiddenAccounts(signal) {
    const resp = await apiClient.get(`/api/accounts/hidden`, { signal });
    return resp.data;
}

export async function hideAccount(accountId) {
    const resp = await apiClient.post(`/api/accounts/${accountId}/hide`);
    return resp.data;
}

export async function unhideAccount(accountId) {
    const resp = await apiClient.delete(`/api/accounts/${accountId}/hide`);
    return resp.data;
}

// Connection Health API
export async function getConnectionHealthSummary(signal) {
    const resp = await apiClient.get(`/api/plaid/health`, { signal });
    return resp.data;
}

export async function getConnectionHealthDetails(signal) {
    const resp = await apiClient.get(`/api/plaid/health/details`, { signal });
    return resp.data;
}

export async function checkConnectionHealth(linkId) {
    const resp = await apiClient.post(`/api/plaid/links/${linkId}/check-health`);
    return resp.data;
}

export async function getConnectionsNeedingAttention(signal) {
    const resp = await apiClient.get(`/api/plaid/health/attention-needed`, { signal });
    return resp.data;
}
